import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { BuscarPecasRow, VehiclePlate } from '@core/types';
import { buscarPorVeiculo, opcoesBuscaGuiada, salvarPlaca } from '../lib/consultas';
import { formatarPlaca } from '../lib/placa';
import { mensagemDeErro } from '../lib/supabase';
import { useMutation } from '@tanstack/react-query';
import Aviso from './Aviso';
import Carregando from './Carregando';
import { LISTA_DE_RESULTADOS, ResultadoPeca } from './BuscaPecas';
import type { AcaoNoOndeFica } from './OndeFica';
import SeletorEmDoisGrupos from './SeletorEmDoisGrupos';
import { fotosDasPecas, lugaresDasPecas } from '../lib/consultas';

// Busca guiada, como no portal dos fornecedores: cada escolha libera a
// próxima — montadora → modelo → ano → motor → categoria. Só aparece o que
// EXISTE no catálogo, então o vendedor nunca navega para um beco vazio.
//
// `veiculoInicial` chega da busca por placa: placa conhecida já entra com o
// carro selecionado. `placaParaSalvar` faz o caminho inverso — identificou o
// carro de uma placa nova, um toque grava o vínculo e a próxima visita do
// cliente é instantânea.
//
// `conferindo` é o caso em que o carro veio da consulta externa: o botão não
// pede para SALVAR, pede para CONFIRMAR. A diferença não é cosmética — em
// 17/09/2026 as duas primeiras placas consultadas foram salvas com o palpite da
// API intacto (uma delas com o câmbio errado), porque "Salvar placa X como Y" é
// o que se aperta sem ler. O que a pessoa está afirmando fica em cima, em uma
// linha curta, e o carro embaixo, para conferir antes de apertar.

export interface VeiculoEscolhido {
  montadora: string;
  modelo: string;
  ano: number | null;
  motorizacao: string | null;
}

/** O chip da categoria (Grafite): pílula, o ativo no degrau de cinza e não em
 *  cor; 44 px de alvo no celular. */
const chipDaCategoria = (ativo: boolean) => [
  'inline-flex min-h-11 items-center rounded-full px-3 text-xs font-medium transition-colors sm:min-h-8',
  ativo ? 'bg-edge text-paper' : 'border border-edge text-mute hover:text-paper',
].join(' ');

export default function BuscaGuiada({
  onEscolher,
  veiculoInicial = null,
  placaParaSalvar = null,
  conferindo = false,
  acaoNoOndeFica,
}: {
  onEscolher: (peca: BuscarPecasRow) => void;
  /** O botão da folha "Onde fica" (ver BuscaPecas). */
  acaoNoOndeFica?: AcaoNoOndeFica<BuscarPecasRow>;
  veiculoInicial?: VehiclePlate | null;
  placaParaSalvar?: string | null;
  /** O carro veio da consulta externa e precisa de conferência, não de cadastro. */
  conferindo?: boolean;
}) {
  const [montadora, setMontadora] = useState(veiculoInicial?.montadora ?? '');
  const [modelo, setModelo] = useState(veiculoInicial?.modelo ?? '');
  const [ano, setAno] = useState<string>(veiculoInicial?.ano ? String(veiculoInicial.ano) : '');
  const [motor, setMotor] = useState(veiculoInicial?.motorizacao ?? '');
  const [categoria, setCategoria] = useState('');
  const [placaSalva, setPlacaSalva] = useState(false);

  // A placa pré-preenche uma vez; depois o vendedor manda.
  useEffect(() => {
    if (!veiculoInicial) return;
    setMontadora(veiculoInicial.montadora);
    setModelo(veiculoInicial.modelo);
    setAno(veiculoInicial.ano ? String(veiculoInicial.ano) : '');
    setMotor(veiculoInicial.motorizacao ?? '');
  }, [veiculoInicial]);

  const anoNum = ano ? Number(ano) : null;

  const opcoes = useQuery({
    queryKey: ['busca-guiada', 'opcoes', montadora, modelo, anoNum],
    queryFn: () => opcoesBuscaGuiada({
      montadora: montadora || null,
      modelo: modelo || null,
      ano: anoNum,
    }),
    staleTime: 60_000,
  });

  const anos = useMemo(() => {
    const min = opcoes.data?.ano_min;
    const max = opcoes.data?.ano_max;
    if (!min || !max || max < min) return [];
    // decrescente: carro novo primeiro, que é o que mais chega no balcão
    return Array.from({ length: max - min + 1 }, (_, i) => max - i);
  }, [opcoes.data?.ano_min, opcoes.data?.ano_max]);

  const pronto = Boolean(montadora && modelo);
  const resultados = useQuery({
    queryKey: ['busca-guiada', 'pecas', montadora, modelo, anoNum, motor, categoria],
    queryFn: () => buscarPorVeiculo({
      montadora, modelo, ano: anoNum,
      motorizacao: motor || null,
      categoria: categoria || null,
    }),
    enabled: pronto,
  });

  // As miniaturas dos resultados — mesma mecânica de lote da busca livre.
  const idsDosResultados = (resultados.data ?? []).map((p) => p.product_id);
  const fotos = useQuery({
    queryKey: ['fotos-dos-resultados', idsDosResultados],
    queryFn: () => fotosDasPecas(idsDosResultados),
    enabled: idsDosResultados.length > 0,
    staleTime: 60_000,
  });
  // E os lugares extras, para o "também em" da faixa "Onde fica".
  const lugares = useQuery({
    queryKey: ['lugares-das-pecas', idsDosResultados],
    queryFn: () => lugaresDasPecas(idsDosResultados),
    enabled: idsDosResultados.length > 0,
    staleTime: 60_000,
  });

  const gravarPlaca = useMutation({
    mutationFn: () => salvarPlaca({
      placa: placaParaSalvar!,
      montadora, modelo,
      ano: anoNum, motorizacao: motor || null,
    }),
    onSuccess: () => setPlacaSalva(true),
  });

  const trocar = (nivel: 'montadora' | 'modelo' | 'ano' | 'motor', valor: string) => {
    // Trocar um nível reseta os de baixo — senão sobra filtro órfão.
    if (nivel === 'montadora') { setMontadora(valor); setModelo(''); setAno(''); setMotor(''); setCategoria(''); }
    if (nivel === 'modelo') { setModelo(valor); setAno(''); setMotor(''); setCategoria(''); }
    if (nivel === 'ano') { setAno(valor); setMotor(''); }
    if (nivel === 'motor') setMotor(valor);
    setPlacaSalva(false);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div>
          <label className="rotulo" htmlFor="bg-montadora">Montadora</label>
          <SeletorEmDoisGrupos
            id="bg-montadora"
            valor={montadora}
            aoTrocar={(v) => trocar('montadora', v)}
            comPeca={opcoes.data?.montadoras ?? []}
            doCatalogo={opcoes.data?.montadoras_catalogo ?? []}
            vazio="Escolha…"
          />
        </div>
        <div>
          <label className="rotulo" htmlFor="bg-modelo">Modelo</label>
          <SeletorEmDoisGrupos
            id="bg-modelo"
            valor={modelo}
            aoTrocar={(v) => trocar('modelo', v)}
            comPeca={opcoes.data?.modelos ?? []}
            doCatalogo={opcoes.data?.modelos_catalogo ?? []}
            desabilitado={!montadora}
            vazio={montadora ? 'Escolha…' : '—'}
          />
        </div>
        <div>
          <label className="rotulo" htmlFor="bg-ano">Ano</label>
          <select id="bg-ano" className="campo" value={ano} disabled={!modelo}
                  onChange={(e) => trocar('ano', e.target.value)}>
            <option value="">{modelo ? 'Todos' : '—'}</option>
            {anos.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <label className="rotulo" htmlFor="bg-motor">Motor</label>
          <select id="bg-motor" className="campo" value={motor}
                  disabled={!modelo || (opcoes.data?.motorizacoes.length ?? 0) === 0}
                  onChange={(e) => trocar('motor', e.target.value)}>
            <option value="">Todos</option>
            {(opcoes.data?.motorizacoes ?? []).map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>

      {pronto && (opcoes.data?.categorias.length ?? 0) > 0 && (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setCategoria('')}
                  aria-pressed={categoria === ''}
                  className={chipDaCategoria(categoria === '')}>
            Todas
          </button>
          {(opcoes.data?.categorias ?? []).map((c) => (
            <button key={c} type="button" onClick={() => setCategoria(c)}
                    aria-pressed={categoria === c}
                    className={chipDaCategoria(categoria === c)}>
              {c}
            </button>
          ))}
        </div>
      )}

      {/* Placa nova identificada: um toque grava e a próxima visita é na hora */}
      {placaParaSalvar && pronto && (
        placaSalva ? (
          <Aviso tipo="ok">
            Placa {formatarPlaca(placaParaSalvar)} {conferindo ? 'confirmada' : 'vinculada'} como{' '}
            {montadora} {modelo}. Na próxima, é automático.
          </Aviso>
        ) : (
          <button type="button" className="botao-secundario w-full"
                  disabled={gravarPlaca.isPending}
                  onClick={() => gravarPlaca.mutate()}>
            {gravarPlaca.isPending ? (
              'Salvando…'
            ) : (
              <>
                <span className="block">
                  {conferindo ? 'Confirmo que é este carro' : `Salvar a placa ${formatarPlaca(placaParaSalvar)}`}
                </span>
                {/* O carro embaixo, em letra menor: é o que se confere antes de
                    apertar, e não o que se lê depois. */}
                <span className="mt-0.5 block text-xs font-normal text-dim">
                  {formatarPlaca(placaParaSalvar)} · {montadora} {modelo}
                  {ano ? ` ${ano}` : ''}{motor ? ` · ${motor}` : ''}
                </span>
              </>
            )}
          </button>
        )
      )}
      {gravarPlaca.error ? <Aviso tipo="erro">{mensagemDeErro(gravarPlaca.error)}</Aviso> : null}

      {opcoes.error ? <Aviso tipo="erro">{mensagemDeErro(opcoes.error)}</Aviso> : null}
      {resultados.error ? <Aviso tipo="erro">{mensagemDeErro(resultados.error)}</Aviso> : null}
      {pronto && resultados.isFetching && <Carregando texto="Buscando peças do veículo" />}

      {pronto && resultados.data && (
        resultados.data.length > 0 ? (
          <ul className={LISTA_DE_RESULTADOS}>
            {resultados.data.map((p) => (
              <li key={p.product_id}>
                <ResultadoPeca peca={p} onEscolher={onEscolher}
                               fotos={fotos.data?.filter((f) => f.product_id === p.product_id) ?? []}
                               lugares={lugares.data?.get(p.product_id)}
                               acaoNoOndeFica={acaoNoOndeFica} />
              </li>
            ))}
          </ul>
        ) : (
          !resultados.isFetching && (
            <div className="cartao p-6 text-center text-sm text-mute">
              Nenhuma peça cadastrada para {montadora} {modelo}
              {ano && ` ${ano}`}{categoria && ` em ${categoria}`}.
              As aplicações são preenchidas no cadastro da peça (aba Aplicação).
            </div>
          )
        )
      )}

      {/* Só quando NÃO há nem universo: banco sem o vehicle_catalog populado
          (rodar scripts/fipe-sync.mjs) ou perfil sem papel ativo. Com a FIPE no
          lugar, a lista nunca mais abre vazia — era o que fazia a tela parecer
          quebrada enquanto o catálogo da loja estava sendo montado. */}
      {!montadora && !opcoes.isLoading
        && (opcoes.data?.montadoras.length ?? 0) === 0
        && (opcoes.data?.montadoras_catalogo.length ?? 0) === 0 && (
        <div className="cartao p-6 text-center text-sm text-mute">
          Nenhuma montadora disponível. O catálogo de veículos (FIPE) não foi carregado neste
          banco — rode <span className="font-mono">scripts/fipe-sync.mjs</span>.
        </div>
      )}
    </div>
  );
}
